// SPDX-License-Identifier: AGPL-3.0-or-later
import { openJson, sealJson, type Sealed, type UnlockedKeys } from "@almanac/crypto";
import { api, ApiError, type RemoteRecord } from "./api.js";
import type { CustomCategory, Profile } from "./categories.js";
import type { FeedEntry, PushEntry } from "./events.js";
import {
  emptySnapshot,
  uuid,
  type Account,
  type BudgetPlan,
  type CalendarEvent,
  type FeedPrefs,
  type Group,
  type MonthBundle,
  type NotifyPrefs,
  type Purchase,
  type Snapshot,
} from "./model.js";
import type { PushSettings, PushStatus } from "./api.js";

/** Where the app keeps its data: the encrypted server, or memory for the demo. */
export interface Backend {
  readonly demo: boolean;
  load(): Promise<Snapshot>;
  saveAccount(account: Account): Promise<void>;
  saveMonth(month: MonthBundle): Promise<void>;
  savePlan(plan: BudgetPlan): Promise<void>;
  saveLearned(learned: Record<string, string>): Promise<void>;
  saveEvent(event: CalendarEvent): Promise<void>;
  deleteEvent(id: string): Promise<void>;
  saveFeedPrefs(prefs: FeedPrefs): Promise<void>;
  saveProfile(profile: Profile): Promise<void>;
  saveCustomCategories(custom: CustomCategory[]): Promise<void>;
  /** Turns the calendar feed on (or gives it a new address) and returns its token. */
  startFeed(name: string, entries: FeedEntry[]): Promise<string>;
  updateFeed(entries: FeedEntry[]): Promise<void>;
  stopFeed(): Promise<void>;
  saveNotifyPrefs(prefs: NotifyPrefs): Promise<void>;
  savePurchase(purchase: Purchase): Promise<void>;
  deletePurchase(id: string): Promise<void>;
  saveGroup(group: Group): Promise<void>;
  deleteGroup(id: string): Promise<void>;
  pushStatus(): Promise<PushStatus>;
  savePush(settings: PushSettings, entries: PushEntry[]): Promise<void>;
  updatePushEntries(entries: PushEntry[]): Promise<void>;
  stopPush(): Promise<void>;
  testPush(): Promise<void>;
  /** Queues a phone alert the browser worked out; the server sends each key once. */
  pushAlert(key: string, body: string): Promise<void>;
}

const AREA = {
  account: "transactions",
  month: "transactions",
  learned: "transactions",
  plan: "budgets",
  event: "events",
  feed: "events",
  profile: "budgets",
  categories: "budgets",
  notify: "budgets",
  purchase: "goals",
  group: "groups",
} as const;
type Kind = keyof typeof AREA;

/**
 * The binding string sealed into every record. Because it includes the
 * record id and kind, the server cannot swap one record's ciphertext for
 * another's without decryption failing.
 */
export function recordContext(area: string, kind: string, id: string): string {
  return `almanac/v1/${area}/${kind}/${id}`;
}

export class RemoteBackend implements Backend {
  readonly demo = false;
  private readonly versions = new Map<string, number>();
  private readonly ids = new Map<string, string>();
  private readonly queues = new Map<string, Promise<void>>();

  constructor(private readonly keys: UnlockedKeys) {}

  private key(area: string): Uint8Array {
    const key = this.keys.areas.get(area);
    if (!key) throw new Error(`No key for area ${area}`);
    return key;
  }

  async load(): Promise<Snapshot> {
    const snapshot: Snapshot = emptySnapshot();
    let since = 0;
    const latest = new Map<string, RemoteRecord>();
    for (;;) {
      const page = await api.records(since);
      for (const r of page.records) latest.set(r.id, r);
      since = page.cursor;
      if (!page.more) break;
    }
    for (const r of latest.values()) {
      this.versions.set(r.id, r.version);
      if (r.deleted || !("v" in r.ciphertext)) continue;
      const sealed = r.ciphertext as Sealed;
      const kind = r.kind as Kind;
      if (!(kind in AREA)) continue;
      const value = openJson<unknown>(this.key(r.area), sealed, recordContext(r.area, r.kind, r.id));
      switch (kind) {
        case "account":
          snapshot.accounts.push(value as Account);
          this.ids.set(`account|${(value as Account).id}`, r.id);
          break;
        case "month": {
          const month = value as MonthBundle;
          snapshot.months.push(month);
          this.ids.set(`month|${month.accountId}|${month.month}`, r.id);
          break;
        }
        case "plan":
          snapshot.plan = value as BudgetPlan;
          this.ids.set("plan", r.id);
          break;
        case "learned":
          snapshot.learned = value as Record<string, string>;
          this.ids.set("learned", r.id);
          break;
        case "event":
          snapshot.events.push(value as CalendarEvent);
          this.ids.set(`event|${(value as CalendarEvent).id}`, r.id);
          break;
        case "feed":
          snapshot.feed = value as FeedPrefs;
          this.ids.set("feed", r.id);
          break;
        case "profile":
          snapshot.profile = value as Profile;
          this.ids.set("profile", r.id);
          break;
        case "categories":
          snapshot.custom = value as CustomCategory[];
          this.ids.set("categories", r.id);
          break;
        case "notify":
          snapshot.notify = value as NotifyPrefs;
          this.ids.set("notify", r.id);
          break;
        case "purchase":
          snapshot.purchases.push(value as Purchase);
          this.ids.set(`purchase|${(value as Purchase).id}`, r.id);
          break;
        case "group":
          snapshot.groups.push(value as Group);
          this.ids.set(`group|${(value as Group).id}`, r.id);
          break;
      }
    }
    return snapshot;
  }

  /** Saves one record. Writes to the same record are queued so versions never race. */
  private save(kind: Kind, slot: string, value: unknown): Promise<void> {
    let id = this.ids.get(slot);
    if (!id) {
      id = uuid();
      this.ids.set(slot, id);
    }
    const recordId = id;
    const previous = this.queues.get(recordId) ?? Promise.resolve();
    const next = previous
      .catch(() => undefined)
      .then(async () => {
        const area = AREA[kind];
        const sealed = sealJson(this.key(area), value, recordContext(area, kind, recordId));
        try {
          const saved = await api.putRecord(recordId, area, kind, sealed, this.versions.get(recordId) ?? null);
          this.versions.set(recordId, saved.version);
        } catch (error) {
          // Another device saved first. v0.1 keeps the change made here (last write wins).
          const current = error instanceof ApiError && error.status === 409 ? (error.body as { current?: RemoteRecord }).current : undefined;
          if (!current) throw error;
          const saved = await api.putRecord(recordId, area, kind, sealed, current.version);
          this.versions.set(recordId, saved.version);
        }
      });
    this.queues.set(recordId, next);
    return next;
  }

  saveAccount(account: Account) {
    return this.save("account", `account|${account.id}`, account);
  }
  saveMonth(month: MonthBundle) {
    return this.save("month", `month|${month.accountId}|${month.month}`, month);
  }
  savePlan(plan: BudgetPlan) {
    return this.save("plan", "plan", plan);
  }
  saveLearned(learned: Record<string, string>) {
    return this.save("learned", "learned", learned);
  }
  saveEvent(event: CalendarEvent) {
    return this.save("event", `event|${event.id}`, event);
  }
  saveFeedPrefs(prefs: FeedPrefs) {
    return this.save("feed", "feed", prefs);
  }
  saveProfile(profile: Profile) {
    return this.save("profile", "profile", profile);
  }
  saveCustomCategories(custom: CustomCategory[]) {
    return this.save("categories", "categories", custom);
  }
  saveNotifyPrefs(prefs: NotifyPrefs) {
    return this.save("notify", "notify", prefs);
  }
  savePurchase(purchase: Purchase) {
    return this.save("purchase", `purchase|${purchase.id}`, purchase);
  }

  deleteEvent(eventId: string): Promise<void> {
    return this.remove(`event|${eventId}`);
  }
  deletePurchase(id: string): Promise<void> {
    return this.remove(`purchase|${id}`);
  }
  saveGroup(group: Group) {
    return this.save("group", `group|${group.id}`, group);
  }
  deleteGroup(id: string): Promise<void> {
    return this.remove(`group|${id}`);
  }

  /** Deletes a record, queued behind any save to it still in flight. */
  private remove(slot: string): Promise<void> {
    const recordId = this.ids.get(slot);
    if (!recordId) return Promise.resolve();
    this.ids.delete(slot);
    const previous = this.queues.get(recordId) ?? Promise.resolve();
    const next = previous
      .catch(() => undefined)
      .then(async () => {
        const version = this.versions.get(recordId);
        if (version === undefined) return;
        try {
          await api.deleteRecord(recordId, version);
        } catch (error) {
          const current = error instanceof ApiError && error.status === 409 ? (error.body as { current?: RemoteRecord | null }).current : undefined;
          if (current === undefined) throw error;
          if (current === null || current.deleted) return;
          await api.deleteRecord(recordId, current.version);
        }
        this.versions.delete(recordId);
      });
    this.queues.set(recordId, next);
    return next;
  }

  async startFeed(name: string, entries: FeedEntry[]) {
    return (await api.startFeed(name, entries)).token;
  }
  updateFeed(entries: FeedEntry[]) {
    return api.updateFeed(entries);
  }
  stopFeed() {
    return api.stopFeed();
  }
  pushStatus() {
    return api.pushStatus();
  }
  savePush(settings: PushSettings, entries: PushEntry[]) {
    return api.savePush(settings, entries);
  }
  updatePushEntries(entries: PushEntry[]) {
    return api.updatePushEntries(entries);
  }
  stopPush() {
    return api.stopPush();
  }
  testPush() {
    return api.testPush();
  }
  pushAlert(key: string, body: string) {
    return api.pushAlert(key, body);
  }
}

/** Keeps everything in memory. Used by the demo, which saves nothing. */
export class MemoryBackend implements Backend {
  readonly demo = true;
  constructor(private readonly seed: () => Snapshot) {}
  async load() {
    return this.seed();
  }
  async saveAccount() {}
  async saveMonth() {}
  async savePlan() {}
  async saveLearned() {}
  async saveEvent() {}
  async deleteEvent() {}
  async saveFeedPrefs() {}
  async saveProfile() {}
  async saveCustomCategories() {}
  async startFeed() {
    return "demo-feed-address-nothing-is-published-in-the-demo";
  }
  async updateFeed() {}
  async stopFeed() {}
  async saveNotifyPrefs() {}
  async savePurchase() {}
  async deletePurchase() {}
  async saveGroup() {}
  async deleteGroup() {}
  // The demo keeps phone settings in memory and never sends anything.
  private push: PushStatus = { enabled: false };
  async pushStatus() {
    return this.push;
  }
  async savePush(settings: PushSettings, entries: PushEntry[]) {
    const { token, ...rest } = settings;
    const hasToken = token === undefined ? this.push.enabled && this.push.hasToken : token !== null;
    this.push = { enabled: true, ...rest, hasToken, entries: entries.length, lastError: null };
  }
  async updatePushEntries(entries: PushEntry[]) {
    if (this.push.enabled) this.push = { ...this.push, entries: entries.length };
  }
  async stopPush() {
    this.push = { enabled: false };
  }
  async testPush() {}
  async pushAlert() {}
}
